/** Docs (UA): https://prostopay.atlassian.net/wiki/spaces/SW/pages/1902346246 — PaymentCT5Screen */

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getCurrencyBySubUnits } from '../../../../shared';
import { IS_LOCAL_DEVELOPMENT } from '../../../../shared/constants';
import { useAppStore } from '../../../../shared/hooks/useAppStore';
import { usePageTracking } from '../../../../shared/hooks/usePageTracking';
import { FeedbackAndReceipt } from '../../../../shared/layouts/FeedbackAndReceipt/FeedbackAndReceipt';
import { getVmDetails } from '../../../../shared/lib/general';
import { ButtonBox, ContentBox, ErrorText, Input, InputWrapper, ScreenTitle, ScreenWrapper } from '../../../../shared/ui';
import { EVENT_NAMES, useSendEventLog } from '../../../event-log';
import { LoyaltyPaymentButton } from '../../../loyalty';
import { CancelButton } from '../../../order/components/CancelButton/CancelButton';
import { useOrderActions } from '../../../order/hooks/useOrderActions';
import { withOrderHydration } from '../../../order/hooks/withOrderHydration';
import { AmountSummary } from '../../components/AmountSummary/AmountSummary';
import { PaymentButtons } from '../../components/PaymentButtons';
import { PaymentButtonsLoader } from '../../components/PaymentButtonsLoader/PaymentButtonsLoader';
import { useAmountValidation } from '../../hooks/useAmountValidation';
import { useOrderAmount } from '../../hooks/useOrderAmount';
import { useTimerTimeout } from '../../hooks/useTimerTimeout';
import { AMOUNT_INPUT_DEBOUNCE_MS, LIMITS } from '../../payment.constants';
import { calculateDiscountedAmount, normalizeDecimalInput } from '../../payment.utils';
import styles from './PaymentCT5Screen.module.css';

const PaymentCT5ScreenBody = () => {
    const { t } = useTranslation();
    const { onTimerTimeoutHandler } = useOrderActions();

    const vm = useAppStore((s) => s.vm);
    const order = useAppStore((s) => s.order);

    const initialAmount = order?.amount ? getCurrencyBySubUnits(order?.amount) : 0;
    const [inputAmount, setAmount] = useState<number>(initialAmount);
    const [displayAmount, setDisplayAmount] = useState<string>(initialAmount > 0 ? String(initialAmount) : '');
    const [error, setError] = useState<string | null>(null);
    const [hasInteracted, setHasInteracted] = useState<boolean>(false);
    const [isPaymentButtonVisible, setIsPaymentButtonVisible] = useState<boolean>(false);
    const [inputNumber, setInputNumber] = useState<number>(0);
    const { max: maxLimit, validate } = useAmountValidation({ min: LIMITS.PAYMENT_CT5.MIN });
    const { isAmountSent, submitAmount } = useOrderAmount({ debounceMs: AMOUNT_INPUT_DEBOUNCE_MS });
    const { sendEvent } = useSendEventLog();
    const { isCT5 } = getVmDetails(vm);

    useTimerTimeout({ screenName: 'payment-ct5', onTimeout: () => onTimerTimeoutHandler('payment-ct5') });

    usePageTracking('payment CT5');

    // The buyer must not be greeted by "amount is too small" before typing
    // anything, so validation starts only after the first input (SWRN-1800).
    useEffect(() => {
        setError(hasInteracted && displayAmount !== '' ? validate(inputNumber) : null);
    }, [inputNumber, displayAmount, hasInteracted, t]);

    const hasAmountEventFiredRef = useRef(false);
    useEffect(() => {
        if (isAmountSent && !hasAmountEventFiredRef.current) {
            sendEvent({ name: EVENT_NAMES.amountSubmitted.ct5 });
            hasAmountEventFiredRef.current = true;
        }
    }, [isAmountSent]);

    const handleAmountChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const result = normalizeDecimalInput(event.target.value);
        if (result === null) return;

        // Over the ceiling the field snaps to the limit, but the raw value is
        // kept in `inputNumber` so the "amount is too big" error can explain why.
        const isOverLimit = result.value > maxLimit;
        const chargedAmount = isOverLimit ? maxLimit : result.value;

        setHasInteracted(true);
        setInputNumber(result.value);
        setDisplayAmount(isOverLimit ? String(maxLimit) : result.display);
        setAmount(chargedAmount);
        setIsPaymentButtonVisible(chargedAmount >= LIMITS.PAYMENT_CT5.MIN);

        const realAmount = calculateDiscountedAmount(chargedAmount, vm?.campaign?.discount_percentage);
        submitAmount(Number.isNaN(realAmount) ? 0 : realAmount);
    };

    return (
        <ScreenWrapper className={styles.screen}>
            <ScreenTitle>{t('Enter exact price')}</ScreenTitle>
            <ContentBox>
                <div className={styles.hint}>{t('Price is on the machine')}</div>
                <InputWrapper>
                    <Input
                        className={styles.amountInput}
                        type="text"
                        inputMode="decimal"
                        max={maxLimit}
                        step="1"
                        placeholder={t<string>('Amount in currency', {
                            currency: t(`Currency.${vm?.currency_code}`),
                        })}
                        value={displayAmount}
                        onChange={handleAmountChange}
                    />

                    <ErrorText>{error}</ErrorText>
                </InputWrapper>

                {isPaymentButtonVisible || IS_LOCAL_DEVELOPMENT ? (
                    <>
                        <AmountSummary
                            amountInSubunits={order?.amount || 0}
                            inputAmount={inputAmount}
                        />
                        <ButtonBox>
                            {isAmountSent || IS_LOCAL_DEVELOPMENT ? (
                                <div className={styles.paymentActions}>
                                    <LoyaltyPaymentButton
                                        orderId={Number(order?.id)}
                                    />
                                    <PaymentButtons />
                                </div>
                            ) : (
                                <PaymentButtonsLoader />
                            )}
                            {!isCT5 && <CancelButton page="payment-ct5" />}
                        </ButtonBox>
                    </>
                ) : null}

                {/* Feedback is for after the purchase; once an amount is in the field
                    the buyer is paying, so only the receipt action stays. */}
                <FeedbackAndReceipt
                    screenName="payment-ct5"
                    variant="secondary"
                    isFeedbackHidden={displayAmount !== ''}
                />
            </ContentBox>
        </ScreenWrapper>
    );
};

const PaymentCT5Screen = withOrderHydration(PaymentCT5ScreenBody);

export { PaymentCT5Screen };
